import { WizardEvent, WizardAction } from './logger.wizard-event';
import { ILoggingContext } from 'app/interfaces/IPortalServiceResult';

import { LogRow } from './logger.log-row';
import { EventRow } from './logger.event-row';
import { AuthService } from 'app/services/authentication/auth.service';
import { ILogger } from 'app/logger/ILogger';
import { convertObjectToString } from 'app/shared/utils/string.util';
import { Router } from '@angular/router';
import {
    Headers,
    Response
} from 'app/services/http';
import { generateUUID } from 'app/shared/utils/uuid.util';
import { TraceRow } from './logger.trace-row';
import { PageViewRow } from './logger.page-row';
import { MetricRow } from './logger.metric-row';
import { isDevEnvironment } from 'environments/environment.util';
import { GlobalVariables } from 'app/services/globals';
import { Injector } from '@angular/core';

import { AILocal } from './logger.common';
import SeverityLevel = AILocal.SeverityLevel;
import { scrubPII } from 'app/extension/api/utils';
import { IUserInfo } from 'app/interfaces/IAuthenticationContext';
import { isString } from 'lodash';

export abstract class LoggerBaseService implements ILogger {
    protected traces: TraceRow[] = [];
    protected pageViews: PageViewRow[] = [];
    protected events: EventRow[] = [];
    protected metrics: MetricRow[] = [];
    protected piiList: string[] = ['usererror', 'user', 'authorizationfailed'];
    protected readonly trackingParameters: Map<string, string> = new Map<string, string>();
    protected _contextMode: 'root' | 'subContext' = 'subContext';
    private _isGettingCachedUser = false;
    private _hasLoggingContextConfiged = false;

    private get _router(): Router {
        return this._injector.get<Router>(Router);
    }

    protected get isNonRootContext() {
        return this._contextMode === 'subContext';
    }

    /**
     * for nonRoot context only
     * @protected
     */
    protected get _authService(): AuthService {
        return this._injector.get(AuthService);
    }

    protected get hasLoggingContextConfiged(): boolean {
        return this._hasLoggingContextConfiged;
    }

    /**
     * for nonRoot context only
     * @protected
     */
    protected get _cachedUser(): IUserInfo {
        if (this._isGettingCachedUser) {
            // Break possible reursion: LoggerBaseService --> AadAuthProvider --> ADAL logging --> LoggerBaseService
            return undefined;
        }
        this._isGettingCachedUser = true;
        try {
            return this._authService.getCachedUser();
        } finally {
            this._isGettingCachedUser = false;
        }
    }

    constructor(
        protected readonly _injector: Injector
    ) {
    }

    public initializeLoggingContext(): void {
        if (this._shouldInitializeLoggingContext()) {
            const loggingContext = this.getLoggingContextAsync();
            this._setGlobalLoggingContext(loggingContext);
            this._hasLoggingContextConfiged = true;
            this._flushBuffers();
        }
    }

    public getLoggingContextAsync(): ILoggingContext {
        return {
            loggingKey: generateUUID()
        };
    }

    public log(componentName: string, message: string, properties?: { [key: string]: string }): void {
        const trace = new TraceRow(message, SeverityLevel.Information, componentName);
        if (properties != null) {
            Object.keys(properties).forEach((key) => {
                trace.addToProperties(key, properties[key]);
            });
        }
        this._logTrace(trace, this.traces);
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public error(componentName: string, message: any, properties?: { [key: string]: string }): void {
        let messageStr = message instanceof Response
            ? `Response: ${message.status} ${convertObjectToString(message)}`
            : convertObjectToString(message);
        if (messageStr) {
            if (__IS_A365__) {
                if (this.isNonRootContext) {
                    messageStr = scrubPII(messageStr, this._authService);
                }
            } else if (this.piiList.find((pii) => messageStr.toLowerCase().indexOf(pii) >= 0)) {
                messageStr = 'UserError: PII';
            }
        }
        const trace = new TraceRow(messageStr, SeverityLevel.Error, componentName);
        const url = this._router.url;
        trace.addToProperties('url', url.indexOf('?') !== -1 ? url.substring(0, url.indexOf('?')) : url);
        if (properties != null) {
            Object.keys(properties).forEach((key) => {
                trace.addToProperties(key, properties[key]);
            });
        }
        this._logTrace(trace, this.traces);
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public warn(componentName: string, message: any, properties?: { [key: string]: string | number }): void {
        const messageStr: string = convertObjectToString(message);
        const trace = new TraceRow(messageStr, SeverityLevel.Warning, componentName);
        if (properties != null) {
            Object.keys(properties).forEach((key) => {
                trace.addToProperties(key, properties[key]);
            });
        }
        this._logTrace(trace, this.traces);
    }

    public logEvent(event: EventRow): void {
        this._logTrace(event, this.events);
    }

    public logMetric(metric: MetricRow): void {
        this._logTrace(metric, this.metrics);
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public logWizardEvent(step: string, substep: string, page: string, action: WizardAction, message?: string, additionalInfo?: { [key: string]: any }): void {
        const event = new WizardEvent(step, substep, page, action, message, additionalInfo);
        this.logEvent(event);
    }

    public logPageView(): void {
        const url = window.location.href.split('#')[0];
        const pageView = new PageViewRow(document.title, url);
        this._logTrace(pageView, this.pageViews);
    }

    public debug(componentName: string, message: string, ...properties: unknown[]): void {
        if (isDevEnvironment()) {
            // eslint-disable-next-line no-console
            console.debug(componentName, message, ...properties);
        }
    }

    public addTrackingParameter(key: string): void {
        if (!key) {
            return;
        }
        if (!this.trackingParameters.has(key)) {
            this.trackingParameters.set(key, generateUUID());
        }
    }

    public removeTrackingParameter(key: string): void {
        if (!key) {
            return;
        }
        if (this.trackingParameters.has(key)) {
            this.trackingParameters.delete(key);
        }
    }

    public flush(): void {
        // do nothing
    }

    protected abstract _logTrace(row: LogRow, buffer: LogRow[]): void;

    protected abstract _setGlobalLoggingContext(loggingContext: ILoggingContext): void;

    protected _flushBuffers(): void {
        this._flushBuffer(this.traces);
        this._flushBuffer(this.pageViews);
        this._flushBuffer(this.events);
    }

    protected _flushBuffer(buffer: LogRow[]): void {
        if (buffer) {
            buffer.forEach((row) => {
                row.pushToAppInsights();
            });
            buffer = [];
        }
    }

    /**
     * for nonRoot context only
     * @protected
     */
    protected async _getDefaultHeadersAsync(): Promise<Headers> {
        const headers = new Headers();
        headers.append('Authorization', `Bearer ${await this._authService.getToken()}`);
        headers.append('Content-Type', 'application/json');
        return headers;
    }

    protected _removeSensitiveStrings(row: LogRow) {
        /**
         * #### region begin ####
        * The code below redacts sensitive strings from the resourceName in log events.
        * We should remove the block below if we can ensure that the resourceName is no longer sensitive.
        * Some code may mistakenly pass the resource entity instead of the resourceName; we need to check and fix this.
         */
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let resourceName: string = (row as any)._properties?.resourceName;
        if (isString(resourceName)) {
            // Redact commonly found sensitive strings from resourceName
            resourceName = this._replaceStr(resourceName, '&sig=', '\'');
            resourceName = this._replaceStr(resourceName, '&sig=', '"');
            resourceName = resourceName?.substring(0, 140);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (row as any)._properties.resourceName = resourceName;
        } else {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            delete (row as any)._properties.resourceName;
        }
        /**
         * #region end
         */

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let message: string = (row as any).message;
        if (message) {
            // Redact commonly found sensitive strings from message
            message = this._replaceStr(message, '&sig=', '%3D');
            message = this._replaceStr(message, '&sig=', '"');
            message = this._replaceStr(message, '&sig=', '\'');
            message = this._replaceStr(message, '&sig=', '.');
            message = this._replaceStr(message, 'password\\\\\\": \\\\\\"', '\\\\\\",');
            message = this._replaceStr(message, 'password\\\\\\":\\\\\\"', '\\\\\\",');
            message = this._replaceStr(message, '"Bearer ', '"}');
            message = this._replaceStr(message, 'secret": "', '"');
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (row as any).message = message;
        }
    }

    private _replaceStr(sourceString: string, startString: string, endString: string): string | undefined {

        if (!sourceString) {
            return undefined;
        }

        const regex = new RegExp(`${startString}(.*?)${endString}`, 'g');
        return sourceString.replace(regex, `${startString}<redacted>${endString}`);
    }

    private _shouldInitializeLoggingContext(): boolean {
        if (__IS_A365__ && !GlobalVariables.enableAriaLogger) {
            return !appInsights.loggingContext || !appInsights.loggingContext.loggingKey && !GlobalVariables.enableIFrameMode;
        }
        return !oneDSWeb?.loggingContext?.loggingKey;
    }
}
